import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef } from "react";

import { GRID_DEFAULTS } from "./constants";
import { useTableColumns, useTableDefaults, useTableHandlers } from "./hooks";
import { SheetProvider, useSheetStore } from "./sheet-context";
import { SheetProps, SheetRowData } from "./types";

import "./styles.css";

import { cn } from "@/lib/utils";

// ────────────────────────────────────────────
// Internal table (consumes the MobX store via context)
// ────────────────────────────────────────────

const SheetInternal = observer(
  <T extends SheetRowData>({
    rowData,
    columns,
    getRowId,

    // Config groups
    editing,
    undoRedo,
    selection,
    clipboard,
    display,

    // Top-level props
    className,
    loading = false,
    gridOptions: customGridOptions,
    rowNumbers,
    rowBuffer,
    suppressOutsideClickHandling = false,

    // Callbacks
    onCellValueChanged,
    onCellEditRequest,
    onCellEditingStarted,
    onCellEditingStopped,
    onUndoStarted,
    onUndoEnded,
    onRedoStarted,
    onRedoEnded,
    onCutStart,
    onCutEnd,
    onPasteStart,
    onPasteEnd,
    onCellSelectionDeleteStart,
    onCellSelectionDeleteEnd,
    onGridReady,
    getRowClass,

    // AgGridReactProps passthrough
    ...restProps
  }: SheetProps<T>) => {
    const store = useSheetStore();
    const gridRef = useRef<AgGridReact<T>>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (suppressOutsideClickHandling) {
          return;
        }

        const container = containerRef.current;
        if (!container) {
          return;
        }
        if (container.contains(event.target as Node)) {
          return;
        }

        const api = store.gridApi;
        if (!api) {
          return;
        }

        api.stopEditing();
        api.clearFocusedCell();
        api.clearCellSelection();

        if (
          container.contains(document.activeElement) &&
          document.activeElement instanceof HTMLElement
        ) {
          document.activeElement.blur();
        }
      },
      [store, suppressOutsideClickHandling],
    );

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [handleClickOutside]);

    const { columnDefs } = useTableColumns<T>({
      columns,
      enableSorting: display?.enableSorting ?? true,
      enableResizing: display?.enableResizing ?? true,
      enableFiltering: display?.enableFiltering ?? false,
    });

    const { defaultColDef, gridOptions, containerStyle } = useTableDefaults({
      editing,
      selection,
      clipboard,
      display,
      emptyStateMessage: display?.emptyStateMessage ?? GRID_DEFAULTS.emptyStateMessage,
      rowNumbers: rowNumbers ?? display?.rowNumbers,
      gridOptions: customGridOptions,
    });

    const {
      getRowIdCallback,
      handleGridReady,
      handleCellEditingStarted,
      handleCellEditingStopped,
      handleCellValueChanged,
      handleCellEditRequest,
      handleUndoStarted,
      handleUndoEnded,
      handleRedoStarted,
      handleRedoEnded,
      handleCutStart,
      handleCutEnd,
      handlePasteStart,
      handlePasteEnd,
      handleCellSelectionDeleteStart,
      handleCellSelectionDeleteEnd,
    } = useTableHandlers<T>({
      store,
      onCellValueChanged,
      onCellEditRequest,
      onCellEditingStarted,
      onCellEditingStopped,
      onUndoStarted,
      onUndoEnded,
      onRedoStarted,
      onRedoEnded,
      onCutStart,
      onCutEnd,
      onPasteStart,
      onPasteEnd,
      onCellSelectionDeleteStart,
      onCellSelectionDeleteEnd,
      onGridReady,
      getRowId,
    });

    return (
      <div
        ref={containerRef}
        className={cn("pl-sheet", className)}
        style={{
          width: "100%",
          height: "100%",
          ...containerStyle,
        }}
      >
        <AgGridReact<T>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          theme={themeQuartz}
          rowBuffer={rowBuffer ?? GRID_DEFAULTS.rowBuffer}
          loading={loading}
          undoRedoCellEditing={undoRedo?.enabled ?? true}
          undoRedoCellEditingLimit={undoRedo?.limit ?? GRID_DEFAULTS.undoRedoLimit}
          getRowId={getRowIdCallback}
          getRowClass={getRowClass}
          onGridReady={handleGridReady}
          onCellEditingStarted={handleCellEditingStarted}
          onCellEditingStopped={handleCellEditingStopped}
          onCellValueChanged={handleCellValueChanged}
          onCellEditRequest={handleCellEditRequest}
          onUndoStarted={handleUndoStarted}
          onUndoEnded={handleUndoEnded}
          onRedoStarted={handleRedoStarted}
          onRedoEnded={handleRedoEnded}
          onCutStart={handleCutStart}
          onCutEnd={handleCutEnd}
          onPasteStart={handlePasteStart}
          onPasteEnd={handlePasteEnd}
          onCellSelectionDeleteStart={handleCellSelectionDeleteStart}
          onCellSelectionDeleteEnd={handleCellSelectionDeleteEnd}
          suppressContextMenu
          {...restProps}
        />
      </div>
    );
  },
);

SheetInternal.displayName = "SheetInternal";

// ────────────────────────────────────────────
// Public components
// ────────────────────────────────────────────

/**
 * Unwrapped table that expects a `SheetProvider` ancestor.
 * Use this when you need sibling components (toolbars, status bars)
 * to share the same MobX store as the table.
 *
 * ```tsx
 * <SheetProvider>
 *   <MyToolbar />            // calls useSheetStore()
 *   <SheetContent … />
 * </SheetProvider>
 * ```
 */
export const SheetContent = SheetInternal;

/**
 * Self-contained table that creates its own `SheetProvider`.
 * Use this when the table is standalone and no external component
 * needs access to the store.
 */
export const Sheet = <T extends SheetRowData>(props: SheetProps<T>) => (
  <SheetProvider>
    <SheetInternal<T> {...props} />
  </SheetProvider>
);

// ────────────────────────────────────────────
// Re-exports
// ────────────────────────────────────────────

export { SheetProvider, useSheetStore } from "./sheet-context";
export { SheetStore } from "./sheet-store";

export type {
  ClipboardConfig,
  DisplayConfig,
  EditingConfig,
  InvalidEditValueMode,
  SelectionConfig,
  SheetColumn,
  SheetProps,
  SheetRowData,
  UndoRedoConfig,
  ValidationErrorsFn,
} from "./types";
